import { type RefCallback, useCallback, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { useCallbackRef } from './ref-hooks'

type State = 'mounted' | 'entering' | 'visible' | 'exiting' | 'unmounted'
type Action = 'MOUNT' | 'ENTER' | 'EXIT' | 'TRANSITION_END' | 'UNMOUNT'

/**
 * Single-class transitioning hook.
 *
 * Step 1: add a class style (`.hidden` works) with your usual hidden styles like opacity 0 + a transition for opacity.
 * Step 2: use hook
 *
 * @param showing The prop controlling whether the element is currently showing.
 * @example
 *
 *   const [ref, rendering, hidden] = useCssTransition(props.showing)
 *   return (
 *     rendering && <div className={cx(styles.root, hidden && styles.hidden)} ref={ref} />
 *   )`
 */
export function useCssTransition(
  showing: boolean,
): [RefCallback<HTMLElement>, true | null, boolean] {
  const initialState = showing ? 'mounted' : 'unmounted'
  const [state, setState] = useState<State>(initialState)

  const send = useCallback(
    (action: Action) =>
      setState(
        (currentState) => transitionState(currentState, action) ?? currentState,
      ),
    [setState],
  )

  // Subscribe to transition events to notify of state changes.
  const ref = useCallbackRef<HTMLElement>(
    useCallback(
      (node: HTMLElement) => {
        const handleTransitionEnd = (event: TransitionEvent) => {
          if (event.target === node) {
            // With React 18 concurrency this update is applied
            // a frame after the transition ends, creating a flash
            // of visible content. By manually flushing we ensure they sync within
            // a frame, removing the flash.
            flushSync(() => send('TRANSITION_END'))
          }
        }

        node.addEventListener('transitioncancel', handleTransitionEnd)
        node.addEventListener('transitionend', handleTransitionEnd)

        // Once we have a node, we're considered mounted,
        // so trigger the `ENTER` transition.
        send('ENTER')

        return () => {
          node.removeEventListener('transitioncancel', handleTransitionEnd)
          node.removeEventListener('transitionend', handleTransitionEnd)
          // Transition to the unmounted state if the node is removed prematurely.
          send('TRANSITION_END')
        }
      },
      [send],
    ),
  )

  // Handle changes to the source indicating whether we are showing.
  const [prevShowing, setPrevShowing] = useState(showing)
  if (prevShowing !== showing) {
    setPrevShowing(showing)
    setState(
      (currentState) =>
        transitionState(currentState, showing ? 'MOUNT' : 'EXIT') ??
        currentState,
    )
  }

  // Sometimes transition events aren't triggered during tests due to how
  // quick stuff happens. As an insurance we'll make sure that we end up in
  // the unmounted state eventually using a timer.
  useEffect(() => {
    if (state === 'exiting') {
      // No transition should run for more than 500ms.
      const timeout = setTimeout(() => {
        send('UNMOUNT')
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [state, send])

  const rendering = state !== 'unmounted'
  const hidden = state !== 'visible' && state !== 'entering'

  return [ref, rendering ? true : null, hidden]
}

function transitionState(state: State, action: Action): State | null {
  switch (state) {
    case 'mounted': {
      switch (action) {
        case 'ENTER':
          return 'entering'
        case 'UNMOUNT':
          return 'unmounted'
        case 'EXIT':
          return 'exiting'
      }
      break
    }
    case 'entering': {
      switch (action) {
        case 'TRANSITION_END':
          return 'visible'
        case 'UNMOUNT':
          return 'unmounted'
        case 'EXIT':
          return 'exiting'
      }
      break
    }
    case 'visible': {
      switch (action) {
        case 'EXIT':
          return 'exiting'
        case 'UNMOUNT':
          return 'unmounted'
      }
      break
    }
    case 'exiting': {
      switch (action) {
        case 'MOUNT':
          return 'visible'
        case 'ENTER':
          return 'entering'
        case 'TRANSITION_END':
        case 'UNMOUNT':
          return 'unmounted'
      }
      break
    }
    case 'unmounted': {
      switch (action) {
        case 'MOUNT':
          return 'mounted'
      }
    }
  }

  return null
}
